import { type RefObject, useEffect, useRef } from 'react'

type UseFollowNewestOnUpdate = (params: {
  scrollRef: RefObject<HTMLElement | null>
  /** Id of the currently-newest item (typically `items[0]?.id` when sorted newest-first). */
  newestId: string | undefined
  /** Skip the follow until initial scroll has settled. */
  enabled: boolean
  /** Read at the moment a new newest arrives — if false, scroll is left alone. */
  isAtBottomRef: RefObject<boolean>
  /**
   * One-shot override: when `true` at the moment a new newest arrives, the
   * container follows to the bottom regardless of `isAtBottomRef`, then the
   * flag is consumed (reset to `false`). Used so a message the local user just
   * sent always scrolls into view even if they had scrolled up.
   */
  forceFollowRef?: RefObject<boolean>
}) => void

/**
 * Pins the scroll container to the bottom for ~1s whenever `newestId` changes,
 * but only if the user was at the bottom at the moment of the change (or
 * `forceFollowRef` is armed — e.g. the local user just sent a message).
 *
 * The 1s pin window absorbs any async growth of the newly arrived row (e.g.
 * a freshly mounted chat message whose decrypted text fills in over several
 * frames). Bails on user scroll up.
 */
export const useFollowNewestOnUpdate: UseFollowNewestOnUpdate = ({
  scrollRef,
  newestId,
  enabled,
  isAtBottomRef,
  forceFollowRef,
}) => {
  const prevNewestIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!enabled) {
      return
    }
    const prev = prevNewestIdRef.current
    prevNewestIdRef.current = newestId
    if (!prev || !newestId || newestId === prev) {
      return
    }
    // Consume the one-shot force flag so it only affects this single update.
    const forceFollow = forceFollowRef?.current ?? false
    if (forceFollowRef) {
      forceFollowRef.current = false
    }
    if (!forceFollow && !isAtBottomRef.current) {
      return
    }
    const el = scrollRef.current
    if (!el) {
      return
    }

    const FRAMES = 60 // ~1s
    let framesLeft = FRAMES
    let lastWrittenScrollTop: number | null = null
    let rafId: number | null = null

    const tick = () => {
      if (lastWrittenScrollTop !== null && el.scrollTop < lastWrittenScrollTop - 5) {
        rafId = null
        return
      }
      el.scrollTop = el.scrollHeight
      lastWrittenScrollTop = el.scrollTop
      framesLeft--
      if (framesLeft > 0) {
        rafId = requestAnimationFrame(tick)
      } else {
        rafId = null
      }
    }
    rafId = requestAnimationFrame(tick)
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [newestId, enabled, scrollRef, isAtBottomRef, forceFollowRef])
}
